import React, { ChangeEventHandler, useEffect, useState } from 'react'
import styled from 'styled-components'

import { removeQuery } from '@hozana/api/actions'
import { fetchApi } from '@hozana/api/fetchApi'
import { useApiData } from '@hozana/api/hooks/useApiData'
import { useDispatch, useSelector } from '@hozana/redux/hooks'
import { useRouter } from '@hozana/router'
import { mediaQuery } from '@hozana/styling/responsive'
import { QA } from '@hozana/tests/constants'

import { Button } from 'elements/button/Button'
import { RoundButton } from 'elements/button/RoundButton'
import { Input } from 'elements/input/Input'
import { AbsoluteDiv } from 'elements/layout/AbsoluteDiv'
import { Block } from 'elements/layout/Block'
import { Div } from 'elements/layout/Div'
import { Li } from 'elements/layout/Li'
import { RoundDiv } from 'elements/layout/RoundDiv'
import { ScrollDiv } from 'elements/layout/ScrollDiv'
import { Span } from 'elements/layout/Span'
import { Ul } from 'elements/layout/Ul'
import { Icon } from 'elements/media/Icon'
import { Ellipsis } from 'elements/text/Ellipsis'
import { Separator } from 'elements/ui/Separator'

import { ICON } from 'config/icons'

import { editCommunityMutation } from 'modules/community/queries'
import type { TCommunity } from 'modules/community/types'
import { getTagsQuery } from 'modules/tag/queries'
import { selectAllTags } from 'modules/tag/selectors'
import type { TTag } from 'modules/tag/types'

const TagChoiceDiv = styled(Div)`
  cursor: pointer;
  &:hover {
    background-color: lightgray;
  }
  ${Span} {
    border: 1px solid ${(props) => props.theme.colors.black30};
  }
`
const StyledUl = styled(Ul)`
  ${mediaQuery('xs', 'sm')} {
    li:last-child {
      margin-right: 150px;
    }
    white-space: nowrap;
  }
`

const StyledButton = styled(Button)`
  position: relative;
  ${AbsoluteDiv} {
    visibility: hidden;
  }
  &:hover {
    ${AbsoluteDiv} {
      visibility: visible;
    }
  }
`

type TTagListProps = {
  keyword: string
  tags: TTag[]
  community: TCommunity
  toggleTag?: (tag: TTag['id']) => void
}

export const TagList: TFC<TTagListProps> = ({ keyword, tags, community, toggleTag }) => {
  const filteredTags =
    keyword !== '' ? tags.filter((tag) => tag?.name.toLowerCase().search(keyword.toLowerCase()) !== -1) : tags

  return (
    <Block h="150px" m="1em 0">
      <ScrollDiv>
        {filteredTags.map((tag, index) => (
          <React.Fragment key={tag.id}>
            {index > 0 && <Separator />}
            <TagChoiceDiv p="8px 10px" onClick={() => toggleTag(tag?.id)}>
              <Span left p="0 1px" m="0 10px 0 0" w="25px" h="25px" borderRadius="3px">
                {community.tags.some((tId) => tId === tag?.id) && <Icon name={ICON.CHECK} />}
              </Span>
              <Ellipsis lineHeight="1.7em">{tag?.name}</Ellipsis>
            </TagChoiceDiv>
          </React.Fragment>
        ))}
      </ScrollDiv>
    </Block>
  )
}

type TTagsFormAdminProps = {
  community: TCommunity
}

export const TagsFormAdmin: TFC<TTagsFormAdminProps> = ({ community }) => {
  const [showList, setShowList] = useState(false)
  const [keyword, setKeyword] = useState('')
  const dispatch = useDispatch()

  const { locale } = useRouter()

  // Useless tags are being filtered in getStaticProps.
  // However, admin should be able to see all tags.
  // So we remove the cache to fetch all of client-side.
  useEffect(() => {
    dispatch(removeQuery('getTags'))
  }, [dispatch])

  useApiData(() => {
    return {
      query: getTagsQuery({
        lang: community.lang !== locale ? community.lang : undefined,
      }),
    }
  }, [community.lang, locale])

  const tags = useSelector((state) => selectAllTags(state))

  const onChangeKeyword: ChangeEventHandler<HTMLInputElement> = (e) => {
    setKeyword(e.target.value)
  }

  const toggleTag = (tagId: number) => {
    let communityTagsIds = community.tags ? [...community.tags] : []
    if (communityTagsIds.find((tId) => tId === tagId)) {
      communityTagsIds = communityTagsIds.filter((tId) => tId !== tagId)
    } else {
      communityTagsIds.push(tagId)
    }

    dispatch(
      fetchApi(
        editCommunityMutation({
          communityId: community.id,
          tags: communityTagsIds,
        }),
      ),
    )
    setKeyword('')
  }

  const setMainTag = (tagId: number) => {
    dispatch(
      fetchApi(
        editCommunityMutation({
          communityId: community.id,
          mainTag: tagId,
        }),
      ),
    )
  }

  const communityTagsIds = community.tags
  if (!communityTagsIds) {
    return null
  }

  return (
    <>
      <Div align="middle" h={{ xs: '36px', md: 'auto' }}>
        <ScrollDiv overflowX="auto" overflowY="hidden" flex="1">
          <StyledUl horizontal>
            {communityTagsIds?.length
              ? communityTagsIds.map((tagId) => {
                  const tag = tags.find((t) => t?.id === tagId)
                  if (tag) {
                    return (
                      <Li key={tagId} m="0 5px 0 0">
                        <StyledButton colors="greyLight" size="small" data-testid={QA.COMMUNITY_PAGE.DESCRIPTION.TAG}>
                          <a onClick={() => setMainTag(tagId)}>
                            <Icon
                              vAlign="text-top"
                              size="1.35em"
                              name={community.mainTag === tagId ? ICON.STAR_ON : ICON.STAR_OFF}
                              color={community.mainTag === tagId ? 'yellow' : 'black50'}
                            />
                          </a>
                          &nbsp;
                          {tag.name}
                          {/* To remove the tag from the community */}
                          <AbsoluteDiv top="5px" right="5px">
                            <RoundDiv p="1px 0" size="20px" bg="red" onClick={() => toggleTag(tagId)}>
                              <Icon name={ICON.CLOSE} color="white" size="0.9em" vAlign="initial" />
                            </RoundDiv>
                          </AbsoluteDiv>
                        </StyledButton>
                      </Li>
                    )
                  }
                  return undefined
                })
              : null}
            {showList && (
              <Li>
                <Input w="100px" h="34px" value={keyword} onChange={onChangeKeyword} />
              </Li>
            )}
            <Li>
              <RoundButton
                size="1.5em"
                icon={showList ? ICON.CLOSE : ICON.PLUS}
                onClick={() => setShowList(!showList)}
                data-testid={QA.COMMUNITY_PAGE.SUPER_ADMIN_ACTIONS.ADD_TAGS_BUTTON}
              />
            </Li>
          </StyledUl>
        </ScrollDiv>
      </Div>
      {tags && showList && <TagList tags={tags} community={community} toggleTag={toggleTag} keyword={keyword} />}
    </>
  )
}
